import { useEffect } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { create } from 'zustand';

import { supabase } from '../config/supabase';

interface AuthState {
  user: User | null;
  session: Session | null;
  isInitialized: boolean;
  setSession: (session: Session | null) => void;
  setInitialized: (v: boolean) => void;
  signOut: () => Promise<void>;
}

const useAuthStore = create<AuthState>((set) => ({
  user: null,
  session: null,
  isInitialized: false,
  setSession: (session) => set({ session, user: session?.user ?? null }),
  setInitialized: (v) => set({ isInitialized: v }),
  signOut: async () => {
    await supabase.auth.signOut();
    set({ session: null, user: null });
  },
}));

export function useAuth() {
  const { user, session, isInitialized, signOut } = useAuthStore();
  return { user, session, isInitialized, signOut };
}

/**
 * Subscribe to Supabase auth state once at the app root. Reads the
 * existing session on mount and pushes every subsequent change into
 * the Zustand store so components can use `useAuth()` synchronously.
 */
export function useAuthInit(): void {
  const setSession = useAuthStore((s) => s.setSession);
  const setInitialized = useAuthStore((s) => s.setInitialized);

  useEffect(() => {
    let mounted = true;
    void supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setSession(data.session ?? null);
      setInitialized(true);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session ?? null);
    });
    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, [setSession, setInitialized]);
}
